import React, { useState } from "react";
import {
  applicationPricing,
  elasticsearchPricing,
  mongodbPricing,
  servicesPricing,
} from "../../data/pricing-page-data";
import { css } from "@emotion/react";
import { pxToRem } from "@upsun/react-component-library/src/styles";
import Heading from "@upsun/react-component-library/src/components/Heading";
import { row } from "../../utils/pricing-page-styles";
import { colors } from "../../utils/colors";
import PricingToggle from "./PricingToggle";

type CPUandMemoryDetailsProps = {
  currency: string;
};

const CPUandMemoryDetails: React.FC<CPUandMemoryDetailsProps> = ({
  currency,
  ...props
}) => {
  const [isHourly, setIsHourly] = useState(true);

  const hr = css`
    margin-bottom: ${pxToRem(8)};
    border-bottom: ${pxToRem(1)} solid ${colors.black600};
  `;

  const rowTop = css`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  `;

  const smallTxt = css`
    margin-top: ${pxToRem(12)};
    font-size: ${pxToRem(12)};
  `;

  const noMargin = css`
    margin-bottom: 0;
  `;

  return (
    <>
      <Heading level={4} text={"CPU and memory"} />
      <div css={rowTop}>
        <p>Billed hourly</p>
        <PricingToggle setIsHourly={setIsHourly} isHourly={isHourly} />
      </div>
      <p css={hr}>
        <strong>Application</strong>
      </p>
      <div css={row}>
        <p css={noMargin}>Shared CPU</p>
        <p css={noMargin}>
          {isHourly
            ? `${applicationPricing.cpuHourly[currency]}*`
            : `${applicationPricing.cpuMonthly[currency]}`}
        </p>
      </div>
      <div css={row}>
        <p css={noMargin}>Guaranteed CPU</p>
        <p css={noMargin}>
          {isHourly
            ? `${applicationPricing.guaranteedCpuHourly[currency]}*`
            : `${applicationPricing.guaranteedCpuMonthly[currency]}`}
        </p>
      </div>
      <div css={row}>
        <p>Memory (1 GB)</p>
        <p>
          {isHourly
            ? `${applicationPricing.memoryHourly[currency]}*`
            : `${applicationPricing.memoryMonthly[currency]}`}
        </p>
      </div>

      <p css={hr}>
        <strong>Task<sup>‡</sup></strong>
      </p>
      <div css={row}>
        <p css={noMargin}>Shared CPU</p>
        <p css={noMargin}>
          {isHourly
            ? `${applicationPricing.cpuHourly[currency]}*`
            : `${applicationPricing.cpuMonthly[currency]}`}
        </p>
      </div>
      <div css={row}>
        <p>Memory (1 GB)</p>
        <p>
          {isHourly
            ? `${applicationPricing.memoryHourly[currency]}*`
            : `${applicationPricing.memoryMonthly[currency]}`}
        </p>
      </div>

      <p css={hr}>
        <strong>Services</strong>
      </p>
      <div css={row}>
        <p css={noMargin}>Shared CPU</p>
        <p css={noMargin}>
          {isHourly
            ? `${servicesPricing.cpuHourly[currency]}*`
            : `${servicesPricing.cpuMonthly[currency]}`}
        </p>
      </div>
      <div css={row}>
        <p css={noMargin}>Guaranteed CPU</p>
        <p css={noMargin}>
          {isHourly
            ? `${servicesPricing.guaranteedCpuHourly[currency]}*`
            : `${servicesPricing.guaranteedCpuMonthly[currency]}`}
        </p>
      </div>
      <div css={row}>
        <p css={noMargin}>Memory (1 GB)</p>
        <p css={noMargin}>
          {isHourly
            ? `${servicesPricing.memoryHourly[currency]}*`
            : `${servicesPricing.memoryMonthly[currency]}`}
        </p>
      </div>
      <div css={row}>
        <p css={noMargin}>Memory (1 GB) - MongoDB (Premium)<sup>†</sup></p>
        <p css={noMargin}>
          {isHourly
            ? `${mongodbPricing.feeHourly[currency]}*`
            : `${mongodbPricing.feeMonthly[currency]}`}
        </p>
      </div>
      <div css={row}>
        <p css={noMargin}>Memory (1 GB) - Elasticsearch (Premium)<sup>†</sup></p>
        <p css={noMargin}>
          {isHourly
            ? `${elasticsearchPricing.feeHourly[currency]}*`
            : `${elasticsearchPricing.feeMonthly[currency]}`}
        </p>
      </div>
      <p css={[smallTxt, noMargin]}>
        {isHourly && `*Resources are billed per second on their allocation. The hourly price
          is an estimation for 732 hours. 732 is the average of 30- and 31-day months.`}
      </p>

      <p css={[smallTxt, noMargin]}>
        <sup>‡</sup> Learn more about tasks and how to configure them in <a href="https://developer.upsun.com/docs/configure-apps/tasks">the Upsun docs</a>.
      </p>

      <p css={[smallTxt, noMargin]}>
        <sup>†</sup> Elasticsearch & MongoDB is provided as a managed service by Upsun. These features require a <a href="/understanding-commitments/">minimal commitment to use</a>.
      </p>
      <p css={[smallTxt, noMargin]}>
        <a href="/contact-us/">Contact sales</a> to discuss.
      </p>
    </>
  );
};

export default CPUandMemoryDetails;
